import type { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { Trans, useLingui } from "@lingui/react/macro"
import type { RecentItem } from "@/lib/search-recents"
import type { SuggestionItem } from "@/types/v2/search"
import { type PaletteCommand, coldStartCommands } from "@/lib/commands"
import { relativeTimeLabel } from "@/lib/relative-time"
import { ResultLeadingIcon } from "./result-leading-icon"
import { HistoryGlyph } from "./search-icons"

// Cold-start view shown when the query is empty (CON-183, with
// the first-run personalization in CON-186). Matches Paper
// artboard 3WP (first run) / FI (returning user).
//
//   - First run (no localStorage history): the "Search or run a
//     command" intro, then a "TRY SEARCHING" chip section, then
//     an "OR RUN A COMMAND" section. CON-186 fills the TRY
//     SEARCHING chips from the business's own recent records
//     instead of hard-coded examples.
//   - Returning user: "RECENT" (entities you opened) and
//     "RECENT SEARCHES" (past queries).
//
// The "OR RUN A COMMAND" chips run real commands from the
// shared registry (CON-184) — the same actions reachable via
// `>` command mode, so the two never drift.

// The cold-start command chips, sourced from the shared command
// registry so they stay in sync with `>` command mode.
const RUN_COMMANDS = coldStartCommands()

interface ColdStartProps {
  recentItems: RecentItem[]
  // Server-seeded chip suggestions, used only on first run.
  suggestions: SuggestionItem[]
  recentSearches: string[]
  // Prefill the input from a try-searching chip / recent search.
  onPrefill: (query: string) => void
  // Record an opened recent item (floats it to the top).
  onOpenItem: (item: RecentItem) => void
  // Run an "Or run a command" chip (navigate / open a form).
  onRunCommand: (command: PaletteCommand) => void
  // Mobile rows get a trailing chevron; desktop omits it.
  showChevron?: boolean
}

export function ColdStart({
  recentItems,
  suggestions,
  recentSearches,
  onPrefill,
  onOpenItem,
  onRunCommand,
  showChevron = false,
}: ColdStartProps) {
  const { i18n } = useLingui()
  const hasHistory =
    recentItems.length > 0 || recentSearches.length > 0

  // First run — no history yet. The intro + command chips are
  // data-independent (so they double as the empty state); the
  // TRY SEARCHING chips appear only when there are records to
  // suggest.
  if (!hasHistory) {
    const chipWrap = showChevron
      ? "flex flex-col gap-2"
      : "flex flex-wrap gap-2"
    return (
      <div className="flex flex-col">
        <div
          data-testid="cold-start-intro"
          className="flex flex-col items-center gap-2 px-6 pb-7 pt-8 text-center"
        >
          <h2 className="tracking-[-0.01em] text-[18px] leading-6 font-semibold text-[#1E1B17]">
            <Trans>Search or run a command</Trans>
          </h2>
          <p className="max-w-[400px] text-[13.5px] leading-[19px] text-[#9A938A]">
            <Trans>
              Find any deal, contact, company, conversation, or
              note. Type {">"} to run a command instead.
            </Trans>
          </p>
        </div>

        {suggestions.length > 0 ? (
          <div data-testid="cold-start-try" className="flex flex-col gap-2 px-1.5">
            <SectionLabel><Trans>Try searching</Trans></SectionLabel>
            <div className={`px-1.5 ${chipWrap}`}>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion.key}
                  type="button"
                  onClick={() => onPrefill(suggestion.title)}
                  data-testid={`try-search-${suggestion.key}`}
                  className={`min-w-0 truncate rounded-full border border-[#E5DFD3] bg-[#FBF8F1] px-3.5 py-2 text-left text-[13px] leading-4 text-[#1E1B17] hover:bg-[#FBF3EA] ${
                    showChevron ? "w-full" : "max-w-full"
                  }`}
                >
                  {suggestion.title}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mx-3 my-3 border-t border-[#F0EDE6]" />

        <div
          data-testid="cold-start-commands"
          className="flex flex-col gap-2 px-1.5 pb-1"
        >
          <SectionLabel><Trans>Or run a command</Trans></SectionLabel>
          <div className={`px-1.5 ${chipWrap}`}>
            {RUN_COMMANDS.map((command) => (
              <button
                key={command.id}
                type="button"
                onClick={() => onRunCommand(command)}
                data-testid={`run-command-${command.id}`}
                className={`flex items-center gap-1.5 rounded-lg bg-[#FBF0E4] px-3 py-2 text-[13px] leading-4 text-[#1E1B17] hover:bg-[#F8E8D5] ${
                  showChevron ? "w-full" : ""
                }`}
              >
                <span className="font-semibold text-brand">{">"}</span>
                {i18n._(command.label)}
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {recentItems.length > 0 ? (
        <div data-testid="cold-start-recent" className="flex flex-col gap-1">
          <SectionLabel><Trans>Recent</Trans></SectionLabel>
          {recentItems.map((item) => (
            <RecentItemRow
              key={item.key}
              item={item}
              onOpen={() => onOpenItem(item)}
              showChevron={showChevron}
            />
          ))}
        </div>
      ) : null}

      {recentSearches.length > 0 ? (
        <div
          data-testid="cold-start-recent-searches"
          className="flex flex-col gap-1"
        >
          <SectionLabel><Trans>Recent searches</Trans></SectionLabel>
          {recentSearches.map((query) => (
            <button
              key={query}
              type="button"
              onClick={() => onPrefill(query)}
              data-testid={`recent-search-${query}`}
              className="flex items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-[#FBF8F1]"
            >
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-[#F0EDE6] bg-[#FAF7F1]">
                <HistoryGlyph size={15} />
              </span>
              <span className="min-w-0 grow truncate text-[13px] leading-[18px] text-[#1E1B17]">
                {query}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

function RecentItemRow({
  item,
  onOpen,
  showChevron,
}: {
  item: RecentItem
  onOpen: () => void
  showChevron: boolean
}) {
  const timeLabel = relativeTimeLabel(item.openedAt)
  return (
    <Link
      href={item.href}
      onClick={onOpen}
      data-testid={`recent-item-${item.key}`}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-left hover:bg-[#FBF8F1]"
    >
      <ResultLeadingIcon type={item.type} title={item.title} />
      <div className="flex min-w-0 grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="min-w-0 grow truncate text-[13.5px] leading-[18px] font-medium text-[#1E1B17]">
            {item.title}
          </span>
          {timeLabel ? (
            <span className="shrink-0 text-[11px] leading-3.5 text-[#9A938A]">
              {timeLabel}
            </span>
          ) : null}
        </div>
        <span className="min-w-0 truncate text-[12px] leading-4 text-[#7A746B]">
          {item.subtitle}
        </span>
      </div>
      {showChevron ? (
        <span className="shrink-0">
          <ChevronRightTiny />
        </span>
      ) : null}
    </Link>
  )
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-3 pb-0.5 pt-1 text-[10px] uppercase leading-3 font-semibold tracking-[0.08em] text-[#9A938A]">
      {children}
    </div>
  )
}

function ChevronRightTiny() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M6 4L10 8L6 12"
        stroke="#C9C2B6"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
